"use client";

import { type ReactNode, useCallback, useState } from "react";
import { Check, ClipboardCheck, FileText, MapPin, Truck } from "@/components/icons";
import { Image } from "@/components/ui/image";
import { formatCents, formatCentsWhole } from "@/lib/formatting/currency";
import { cn } from "@/lib/utils";
import { type ActiveOrder, DELIVERED_PHASE, FADING_PHASE, type MapOrder, cardAnchor, lanePoints, upcomingOrders } from "./landing-hero-orders";
import { CARD_WIDTH_PX, type CardBox, type PlacementSize, placeCards } from "./landing-hero-card-placement";

type Tone = "success" | "brand" | "muted";

interface DocumentRow {
  label: string;
  value: string;
  strong?: boolean;
}

interface CardContent {
  icon: ReactNode;
  kind: string;
  number: string;
  status: string;
  tone: Tone;
  rows: DocumentRow[];
}

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        tone === "success" && "bg-success-bg text-success-fg",
        tone === "brand" && "bg-brand-tint text-brand-strong",
        tone === "muted" && "bg-muted text-fg-muted",
      )}
    >
      {tone === "success" ? <Check aria-hidden className="size-3" strokeWidth={3} /> : null}
      {children}
    </span>
  );
}

function cardContent(order: MapOrder, phase: number): CardContent {
  const delivered = formatCentsWhole(order.deliveredCents);
  const productCents = order.unitPriceCents * order.quantity;
  const lane = `${order.from.city} → ${order.to.city}`;
  const icon = "size-3.5";
  if (phase === 2) {
    return {
      icon: <FileText aria-hidden className={icon} />,
      kind: "Quote",
      number: order.documents.quote,
      status: "Sent",
      tone: "brand",
      rows: [
        { label: `${order.quantity} × ${formatCents(order.unitPriceCents)}`, value: formatCentsWhole(productCents) },
        { label: `Freight · ${order.miles.toLocaleString("en-US")} mi`, value: formatCentsWhole(order.deliveredCents - productCents) },
        { label: "Delivered", value: delivered, strong: true },
      ],
    };
  }
  if (phase === 3) {
    return {
      icon: <ClipboardCheck aria-hidden className={icon} />,
      kind: "Order",
      number: order.documents.order,
      status: "Placed",
      tone: "success",
      rows: [
        { label: `${order.quantity} ${order.noun}`, value: delivered, strong: true },
        { label: "Ship to", value: `${order.to.city}, ${order.to.state}` },
      ],
    };
  }
  const delivering = phase === 4;
  return {
    icon: <Truck aria-hidden className={icon} />,
    kind: "BOL",
    number: order.documents.bol,
    status: delivering ? "In transit" : "Delivered",
    tone: delivering ? "brand" : "success",
    rows: [
      { label: "Lane", value: lane },
      delivering
        ? { label: "Load", value: `${order.quantity} ${order.noun}` }
        : { label: "Signed", value: `${order.to.city} dock` },
    ],
  };
}

function CardBody({ content }: { content: CardContent }) {
  return (
    <span className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-strong">{content.icon}</span>
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-muted">{content.kind}</span>
          <span className="truncate text-[12px] font-semibold tabular-nums">{content.number}</span>
        </span>
        <Pill tone={content.tone}>{content.status}</Pill>
      </span>
      <span className="flex flex-col gap-1 border-t border-border-subtle pt-2">
        {content.rows.map((row) => (
          <span key={row.label} className={cn("flex items-baseline justify-between gap-3", row.strong ? "text-[14px] font-bold" : "text-[12px] text-fg-muted")}>
            <span className="truncate">{row.label}</span>
            <span className="shrink-0 tabular-nums text-foreground">{row.value}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

function ListingCard({ order, uploading }: { order: MapOrder; uploading: boolean }) {
  return (
    <span className="flex flex-col gap-2">
      <span className="flex gap-2.5">
        <span className="relative size-14 shrink-0 overflow-hidden rounded-lg bg-muted">
          <Image
            src={order.photo}
            alt=""
            fill
            sizes="56px"
            className="object-cover duration-[1200ms] ease-out animate-in fade-in zoom-in-110 motion-reduce:animate-none"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[12px] font-semibold leading-tight">{order.title}</span>
          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-fg-muted">
            <MapPin aria-hidden className="size-3 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{`${order.from.city}, ${order.from.state}`}</span>
            {uploading ? null : <Pill tone="success">Live</Pill>}
          </span>
          <span className="mt-auto whitespace-nowrap text-[13px] font-bold tabular-nums">
            {formatCents(order.unitPriceCents)}
            <span className="text-[11px] font-medium text-fg-muted"> / unit at yard</span>
          </span>
        </span>
      </span>
      {uploading ? (
        <span className="flex items-center gap-2">
          <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
            <span className="block h-full w-full rounded-full bg-brand duration-[1300ms] ease-out animate-in slide-in-from-left-full motion-reduce:animate-none" />
          </span>
          <span className="text-[11px] font-semibold text-fg-muted">Uploading</span>
        </span>
      ) : null}
    </span>
  );
}

function CardFace({ order, phase }: { order: MapOrder; phase: number }) {
  if (phase <= 1) return <ListingCard order={order} uploading={phase === 0} />;
  return (
    <div key={phase} className="duration-300 animate-in fade-in motion-reduce:animate-none">
      <CardBody content={cardContent(order, phase)} />
    </div>
  );
}

const LISTING_CARD_HEIGHT_PX = 100;
const DOCUMENT_CARD_HEIGHT_PX = 128;

const CARD_CLASSES = "rounded-xl bg-surface/95 p-2.5 shadow-lg shadow-foreground/10 ring-1 ring-border-subtle backdrop-blur-sm";

function anchorKey(active: ActiveOrder): string {
  return `${active.order.id}-${cardAnchor(active) === active.order.from ? "from" : "to"}`;
}

function MapCard({ active, box }: { active: ActiveOrder; box: CardBox }) {
  const phase = Math.min(active.phase, DELIVERED_PHASE);
  return (
    <div
      style={{ left: box.left, top: box.top, width: CARD_WIDTH_PX }}
      className="absolute"
    >
      <div
        className={cn(
          CARD_CLASSES,
          "transition-opacity duration-700 animate-in fade-in zoom-in-95 slide-in-from-bottom-1 motion-reduce:animate-none",
          active.phase === FADING_PHASE && "opacity-0",
        )}
      >
        <CardFace order={active.order} phase={phase} />
      </div>
    </div>
  );
}

function useElementSize(): [(node: HTMLDivElement | null) => void, PlacementSize] {
  const [size, setSize] = useState<PlacementSize>({ width: 0, height: 0 });
  const ref = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

interface Placement {
  signature: string;
  width: number;
  boxes: Map<string, CardBox>;
}

function usePlacement(orders: ActiveOrder[], size: PlacementSize): { byAge: ActiveOrder[]; boxes: Map<string, CardBox> } {
  const byAge = orders.slice().sort((first, second) => second.phase - first.phase);
  const signature = `${size.width}x${size.height}:${byAge.map(anchorKey).join("|")}`;
  const [placement, setPlacement] = useState<Placement>({ signature: "", width: 0, boxes: new Map() });
  if (size.width > 0 && placement.signature !== signature) {
    const pins = [...orders.map(({ order }) => order), ...upcomingOrders(orders)].flatMap((order) => [order.from.point, order.to.point]);
    const routes = orders.flatMap(({ order }) => lanePoints(order.from.point, order.to.point));
    const requests = byAge.map((active) => {
      const stop = cardAnchor(active);
      const height = stop === active.order.from ? LISTING_CARD_HEIGHT_PX : DOCUMENT_CARD_HEIGHT_PX;
      return { key: anchorKey(active), point: stop.point, preferred: stop.side, height };
    });
    const kept = placement.width === size.width ? placement.boxes : new Map<string, CardBox>();
    const boxes = placeCards(requests, pins, routes, size, kept);
    setPlacement({ signature, width: size.width, boxes });
    return { byAge, boxes };
  }
  return { byAge, boxes: placement.boxes };
}

export function MapCards({ orders }: { orders: ActiveOrder[] }) {
  const [ref, size] = useElementSize();
  const { byAge, boxes } = usePlacement(orders, size);
  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 @max-[660px]:hidden">
      {byAge.map((active) => {
        const box = boxes.get(anchorKey(active));
        return box ? <MapCard key={anchorKey(active)} active={active} box={box} /> : null;
      })}
    </div>
  );
}

export function PhoneCards({ orders }: { orders: ActiveOrder[] }) {
  const shown = orders.filter((active) => active.phase < FADING_PHASE).slice(0, 3);
  return (
    <div className="grid min-h-60 content-start gap-2 border-t border-border-subtle p-4 @min-[660px]:hidden">
      {shown.map((active) => (
        <div key={`${active.order.id}-${active.phase}`} className={cn(CARD_CLASSES, "duration-500 animate-in fade-in motion-reduce:animate-none")}>
          <CardFace order={active.order} phase={active.phase} />
        </div>
      ))}
    </div>
  );
}
